import { Button, Icon, Typography } from 'antd';
import Checkbox, { CheckboxChangeEvent } from 'antd/lib/checkbox';
import Table, { ColumnProps } from 'antd/lib/table';
import { getRoleTypeFamilyDetails } from 'api/RoleTypeApi';
import { useUser } from 'auth/UserHooks';
import ButtonRow from 'components/common/ButtonRow';
import Modal from 'components/common/Modal';
import SavePreferencesButton from 'components/common/SavePreferencesButton';
import Stack from 'components/common/Stack';
import React, { useState, useEffect } from 'react';
import { RoleTypeFamily } from 'types/RoleTypeFamily';
import useLocalStorage from 'utils/useLocalStorage';
import RoleTypeFamilyForm from './RoleTypeFamilyForm';

const RoleTypeFamilyLandingPage = () => {
  const user = useUser();
  const canManageRoleTypes = user.permissions.canManageRoleTypes;
  const [family, setFamily] = useState<RoleTypeFamily[]>([]);
  const [filteredFamily, setFilteredFamily] = useState<RoleTypeFamily[]>([]);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [showInactive, setShowInactive] = useLocalStorage<any>('RoleTypeFamilyLandingPage_InactiveSetting', false);
  const [sortSettings, setSortSettings] = useLocalStorage<any>('RoleTypeFamilyLandingPage_SortSettings', {
    key: 'name',
    order: 'ascend'
  });
  const [loading, setLoading] = useState(false);

  const _refreshFamily = () => {
    return getRoleTypeFamilyDetails().then(response => {
      setFamily(response);
      filterFamily(response, showInactive);
    });
  };

  const filterFamily = (family: RoleTypeFamily[], showInactive: boolean) => {
    setFilteredFamily(family.filter((f: RoleTypeFamily) => showInactive || !f.inactive));
  };

  const handleInactiveChange = (e: CheckboxChangeEvent) => {
    setShowInactive(e.target.checked);
    filterFamily(family, e.target.checked);
  };

  let columns: ColumnProps<RoleTypeFamily>[] = [
    {
      title: 'Name',
      key: 'name',
      dataIndex: 'name',
      sorter: (a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase())
    },
    {
      title: 'Description',
      key: 'description',
      render: (item: RoleTypeFamily) => (
        <Typography.Paragraph style={{ marginBottom: 0 }} ellipsis={{ rows: 2, expandable: false }}>
          {item.description}
        </Typography.Paragraph>
      ),
      sorter: (a, b) => a.description.toLowerCase().localeCompare(b.description.toLowerCase())
    },
    {
      title: 'Sort',
      key: 'displayOrder',
      dataIndex: 'displayOrder',
      sorter: (a, b) => a.displayOrder - b.displayOrder
    },
    {
      title: 'Inactive',
      key: 'inactive',
      render: (item: RoleTypeFamily) => item.inactive && <Icon type="check-circle" />
    }
  ];

  if (canManageRoleTypes) {
    columns.push({
      render: (item: RoleTypeFamily) => (
        <Modal
          item={item}
          trigger={({ openModal }: { openModal: () => void }) => (
            <Button onClick={openModal} size="small" icon="edit">
              Edit
            </Button>
          )}
        >
          {({ item, closeModal }: { item: RoleTypeFamily; closeModal: () => void }) => (
            <RoleTypeFamilyForm
              family={item}
              onSubmit={() => {
                _refreshFamily().then(closeModal);
              }}
              onCancel={closeModal}
            />
          )}
        </Modal>
      )
    });
  }

  const columnsSortSettings = columns.map(column => {
    if (column.key === sortSettings.key) {
      return { ...column, defaultSortOrder: sortSettings.order };
    }
    return column;
  });
  columns = columnsSortSettings;

  function onChange(_pagination: any, _filters: any, sorter: any) {
    if (Object.keys(sorter).length > 0) {
      setSortSettings({
        key: sorter.columnKey,
        order: sorter.order
      });
    }
  }

  useEffect(() => {
    setLoading(true);
    _refreshFamily().then(() => {
      setLoading(false);
    });
  }, []);

  const _handleModalState = () => {
    setShowModal(!showModal);
  };

  return (
    <>
      {canManageRoleTypes && (
        <Modal isOpen={showModal} onClose={_handleModalState}>
          {({ closeModal }: { closeModal: () => void }) => (
            <RoleTypeFamilyForm
              onSubmit={() => {
                _refreshFamily().then(closeModal);
              }}
              onCancel={closeModal}
            />
          )}
        </Modal>
      )}
      <Stack>
        <Typography.Title level={1}>
          Role Type Family
          <ButtonRow style={{ float: 'right' }}>
            {canManageRoleTypes && (
              <Button icon="plus" onClick={_handleModalState}>
                Create Role Type Family
              </Button>
            )}
            <SavePreferencesButton
              keys={['RoleTypeFamilyLandingPage_InactiveSetting', 'RoleTypeFamilyLandingPage_SortSettings']}
              disabled={loading}
            />
          </ButtonRow>
        </Typography.Title>
        <Checkbox checked={showInactive} onChange={handleInactiveChange} style={{ marginBottom: '20px' }}>
          Show Inactive
        </Checkbox>
        <Table
          columns={columns}
          dataSource={filteredFamily}
          loading={loading}
          rowKey="id"
          pagination={false}
          onChange={onChange}
        />
      </Stack>
    </>
  );
};

export default RoleTypeFamilyLandingPage;
